/**
 * Reports API — Supabase PostgREST.
 */

import supabase from "../config/supabase";

const withDisplayName = (person) => {
  if (!person) return null;
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return { ...person, name: name || null };
};

const reportsApi = {
  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("reports")
      .insert({ ...body, reporter_id: user.id })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  async list(params = {}) {
    let query = supabase
      .from("reports")
      .select(
        "*, reporter:users!reporter_id(id, first_name, last_name)",
        { count: "exact" },
      )
      .order("created_at", { ascending: false });

    if (params.status) query = query.eq("status", params.status);
    if (params.entity_type) query = query.eq("entity_type", params.entity_type);

    const limit = params.limit || 50;
    const offset = params.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) return { success: false, error: error.message };

    const items = data || [];
    const serviceIds = [
      ...new Set(
        items
          .filter((row) => row.entity_type === "service")
          .map((row) => row.entity_id)
          .filter(Boolean),
      ),
    ];

    let listingsById = {};
    if (serviceIds.length) {
      const { data: listings } = await supabase
        .from("services")
        .select("id, title, user_id, status, mobile")
        .in("id", serviceIds);
      listingsById = Object.fromEntries(
        (listings || []).map((listing) => [listing.id, listing]),
      );
    }

    return {
      success: true,
      data: items.map((row) => ({
        ...row,
        reporter: withDisplayName(row.reporter),
        listing: listingsById[row.entity_id] || null,
      })),
      total: count || 0,
    };
  },

  async getPendingCount() {
    const { count, error } = await supabase
      .from("reports")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending");

    if (error) return { success: false, error: error.message };
    return { success: true, data: count || 0 };
  },

  async update(reportId, body) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("reports")
      .update({
        ...body,
        resolved_by: user.id,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", reportId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },
};

export default reportsApi;
